import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { Node } from '@antv/x6';
import {
  calculateStateSize,
  calculateObjectSize,
  calculateStatePositions,
  calculateNaturalStateWidths,
  calculateMinParentWidthForNaturalStates,
} from '../utils/sizing';

interface NodeContextMenuProps {
  node: Node;
  nodeType: 'object' | 'process' | 'state' | 'value' | 'option';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  position?: { x: number; y: number };
  anchor?: Element | null;
}

interface MenuItem {
  key: string;
  icon: string;
  label: string;
  danger?: boolean;
}

/** 根据节点状态动态生成菜单项 */
function buildMenu(nodeType: 'object' | 'process' | 'state' | 'value' | 'option', hasSubGraph: boolean): MenuItem[] {
  const items: MenuItem[] = [];

  if (nodeType === 'object' || nodeType === 'option') {
    items.push({ key: 'add-state', icon: '＋', label: '添加状态' });
  }

  if (hasSubGraph) {
    items.push({ key: 'enter-subgraph', icon: '⊡', label: '进入子图' });
  } else {
    items.push({ key: 'sub-graph', icon: '⊞', label: '新建子图' });
  }

  items.push({ key: 'copy', icon: '⎘', label: '复制' });
  items.push({ key: 'delete', icon: '✕', label: '删除', danger: true });

  return items;
}

const NodeContextMenu: React.FC<NodeContextMenuProps> = ({
  node,
  nodeType,
  open: openProp,
  onOpenChange,
  position,
  anchor,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const setOpen = useCallback(
    (value: boolean) => {
      if (isControlled) {
        onOpenChange?.(value);
      } else {
        setInternalOpen(value);
      }
    },
    [isControlled, onOpenChange]
  );

  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(position ?? null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (position) setMenuPos(position);
  }, [position]);

  const hasSubGraph = !!(node.getData()?.hasSubGraph);
  const items = buildMenu(nodeType, hasSubGraph);

  const getGraph = useCallback(() => {
    return (node as any).model?.graph ?? (window as any).__OPM_GRAPH__;
  }, [node]);

  const getSubGraphCtx = useCallback(() => {
    return (window as any).__OPM_SUBGRAPH__;
  }, []);

  const updateMenuPos = useCallback(() => {
    const triggerEl = anchor || buttonRef.current;
    if (!triggerEl) return;
    const rect = triggerEl.getBoundingClientRect();
    setMenuPos({ x: rect.right + 6, y: rect.top });
  }, [anchor]);

  // 当画布视图变化（平移/缩放/节点移动）时重新定位菜单
  useEffect(() => {
    if (!open) return;
    updateMenuPos();
    const graph = getGraph();
    const onScale = () => updateMenuPos();
    const onTranslate = () => updateMenuPos();
    graph.on('scale', onScale);
    graph.on('translate', onTranslate);
    return () => {
      graph.off('scale', onScale);
      graph.off('translate', onTranslate);
    };
  }, [open, updateMenuPos, getGraph]);

  // 点击外部区域时关闭菜单
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const triggerEl = anchor || buttonRef.current;
      const isOutsideMenu = !menuRef.current || !menuRef.current.contains(e.target as HTMLElement);
      const isOutsideTrigger = !triggerEl || !triggerEl.contains(e.target as HTMLElement);
      if (isOutsideMenu && isOutsideTrigger) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [open]);

  const handleAction = useCallback((action: string) => {
    setOpen(false);
    const graph = getGraph();
    if (!graph) return;

    const subGraphCtx = getSubGraphCtx();

    const updateObjectLabelAnchor = (targetNode: any, stateCount: number) => {
      const targetType = targetNode?.getData()?.type;
      if (targetType !== 'object' && targetType !== 'option') return;
      targetNode.attr('label/refY', stateCount > 0 ? 26 : '55%');
      targetNode.attr(
        'label/textVerticalAnchor',
        stateCount > 0 ? 'top' : 'middle'
      );
    };

    switch (action) {
      case 'add-state': {
        if (nodeType !== 'object' && nodeType !== 'option') break;
        const pos = node.getPosition();
        const children = node.getChildren() || [];
        const existingStates = children.filter((c: any) => c.getData()?.type === 'state');
        const stateCount = existingStates.length;

        // 新状态的标签和尺寸
        const stateLabel = `State ${stateCount + 1}`;
        const stateSize = calculateStateSize(stateLabel);

        // 创建新的状态节点（位置将在所有位置计算完成后设置）
        const newStateId = `state-${Date.now()}`;
        const stateNode = graph.addNode({
          id: newStateId,
          shape: 'opm-state',
          x: pos.x,
          y: pos.y,
          width: stateSize.width,
          height: stateSize.height,
          data: {
            label: stateLabel,
            type: 'state',
            essence: 'informational',
            active: false,
            executionState: 'idle',
            parentId: node.id,
          },
        });
        node.embed(stateNode);

        // 收集所有状态节点（包括新添加的）并重新定位它们
        const allStates = [...existingStates, stateNode];

        // 根据标签计算所有状态的自然宽度
        const allLabels = allStates.map((s: any) => s.getData()?.label || '');
        const naturalWidths = calculateNaturalStateWidths(allLabels);
        const minStatesWidth = calculateMinParentWidthForNaturalStates(naturalWidths);
        const currentData = node.getData() || {};
        const parentLabel = currentData.label || 'Object';
        const labelSize = calculateObjectSize(parentLabel, true);
        const neededWidth = Math.max(minStatesWidth, labelSize.width);
        node.resize(neededWidth, node.getSize().height);

        // 使用自然宽度计算所有状态的位置
        const positions = calculateStatePositions(
          pos,
          neededWidth,
          allStates.length,
          naturalWidths,
          stateSize.height,
        );

        // 重新定位和调整每个状态节点的尺寸
        allStates.forEach((s: any, i: number) => {
          s.setPosition({ x: positions[i].x, y: positions[i].y });
          s.resize(positions[i].width, stateSize.height);
        });

        // 重新计算父节点尺寸以容纳所有子节点
        const allChildren = node.getChildren() || [];
        const childBounds = allChildren.map((child: any) => {
          const childPos = child.getPosition();
          const childSize = child.getSize();
          return {
            x: childPos.x - pos.x,
            y: childPos.y - pos.y,
            width: childSize.width,
            height: childSize.height,
          };
        });
        const newSize = calculateObjectSize(parentLabel, true, childBounds);
        node.resize(newSize.width, newSize.height);

        // 更新 childStateCount 以触发 ObjectNode 重新渲染
        node.setData({ childStateCount: allStates.length });
        updateObjectLabelAnchor(node, allStates.length);
        break;
      }
      case 'sub-graph': {
        // 使用子图系统创建子图
        if (subGraphCtx) {
          const subGraphId = node.getData()?.subGraphId;
          if (subGraphId) {
            subGraphCtx.navigateToGraph(subGraphId);
          } else {
            subGraphCtx.createSubGraph(node.id);
          }
        }
        break;
      }
      case 'enter-subgraph': {
        // 进入已有的子图
        if (subGraphCtx) {
          const subGraphId = node.getData()?.subGraphId;
          if (subGraphId) {
            subGraphCtx.navigateToGraph(subGraphId);
          }
        }
        break;
      }
      case 'copy': {
        const data = { ...node.getData() };
        delete data.selected;
        delete data.dragTarget;
        const pos = node.getPosition();
        const size = node.getSize();

        // 复制主节点
        const newNode = graph.addNode({
          shape: (node as any).shape,
          x: pos.x + 30,
          y: pos.y + 30,
          width: size.width,
          height: size.height,
          data: { ...data, label: `${data.label}` },
        });

        // 如果该节点有子节点，则一并复制
        const children = node.getChildren() || [];
        if (children.length > 0) {
          children.forEach((child: any) => {
            const childData = { ...child.getData() };
            delete childData.selected;
            delete childData.dragTarget;
            const childPos = child.getPosition();
            const childSize = child.getSize();

            // 计算相对于父节点的偏移量
            const relX = childPos.x - pos.x;
            const relY = childPos.y - pos.y;

            const newChildPos = {
              x: pos.x + 30 + relX,
              y: pos.y + 30 + relY,
            };

            const newChild = graph.addNode({
              shape: (child as any).shape,
              x: newChildPos.x,
              y: newChildPos.y,
              width: childSize.width,
              height: childSize.height,
              data: {
                ...childData,
                label: `${childData.label}`,
                parentId: newNode.id,
              },
            });
            newNode.embed(newChild);
          });
        }

        // 选中新复制的节点
        graph.getNodes().forEach((n: any) => n.setData({ selected: false }));
        newNode.setData({ selected: true });
        break;
      }
      case 'delete': {
        graph.getEdges().forEach((edge: any) => {
          const src = edge.getSourceCellId?.();
          const tgt = edge.getTargetCellId?.();
          if (src === node.id || tgt === node.id) {
            graph.removeCell(edge);
          }
        });
        const children = [...(node.getChildren() || [])];
        children.forEach((child: any) => {
          node.unembed(child);
          graph.removeCell(child);
        });
        // 如果删除的是状态子节点，则重新定位剩余状态并调整父节点尺寸
        const parentId = node.getData()?.parentId;
        if (parentId && node.getData()?.type === 'state') {
          const parent = graph.getCellById(parentId);
          if (parent?.isNode()) {
            const parentPos = parent.getPosition();
            const remainingStates = (parent.getChildren() || []).filter(
              (c: any) => c.getData()?.type === 'state',
            );
            // 使用自然宽度重新定位剩余的状态节点
            if (remainingStates.length > 0) {
              const remainingLabels = remainingStates.map((s: any) => s.getData()?.label || '');
              const naturalWidths = calculateNaturalStateWidths(remainingLabels);
              const minStatesWidth = calculateMinParentWidthForNaturalStates(naturalWidths);
              const parentLabel = parent.getData()?.label || 'Object';
              const labelSize = calculateObjectSize(parentLabel, true);
              const neededWidth = Math.max(minStatesWidth, labelSize.width);
              const stateSize = remainingStates[0].getSize();
              const positions = calculateStatePositions(
                parentPos,
                neededWidth,
                remainingStates.length,
                naturalWidths,
                stateSize.height,
              );
              remainingStates.forEach((s: any, i: number) => {
                s.setPosition({ x: positions[i].x, y: positions[i].y });
                s.resize(positions[i].width, stateSize.height);
              });
              // 调整父节点宽度以适应状态的自然宽度
              parent.resize(neededWidth, parent.getSize().height);
            }
            // 根据子节点边界调整父节点高度
            const allChildren = parent.getChildren() || [];
            const childBounds = allChildren.map((child: any) => {
              const childPos = child.getPosition();
              const childSize = child.getSize();
              return {
                x: childPos.x - parentPos.x,
                y: childPos.y - parentPos.y,
                width: childSize.width,
                height: childSize.height,
              };
            });
            const parentLabel = parent.getData()?.label || 'Object';
            const hasStates = remainingStates.length > 0;
            const newSize = calculateObjectSize(parentLabel, hasStates, childBounds);
            parent.resize(parent.getSize().width, newSize.height);

            // 更新 childStateCount 以触发父 ObjectNode 重新渲染
            parent.setData({ childStateCount: remainingStates.length });
            updateObjectLabelAnchor(parent, remainingStates.length);
          }
        }
        graph.removeCell(node);
        break;
      }
    }
  }, [node, nodeType, getGraph, getSubGraphCtx]);

  return (
    <>
      {/* 触发按钮保留在节点内部（仅非受控且没有外部 anchor 时显示） */}
      {!isControlled && !anchor && (
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          if (!open) updateMenuPos();
          setOpen(!open);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          top: 2,
          right: 2,
          zIndex: 10,
          pointerEvents: 'auto',
          width: 20,
          height: 20,
          borderRadius: '50%',
          border: '1px solid #d9d9d9',
          background: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 13,
          color: '#595959',
          padding: 0,
          boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
          transition: 'all 0.15s',
          lineHeight: 1,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#f0f0f0';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = '#fff';
        }}
      >
        ⋯
      </button>
      )}

      {/* 通过 Portal 渲染菜单，以脱离节点的层叠上下文 */}
      {open && menuPos && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: menuPos.y,
            left: menuPos.x,
            minWidth: 120,
            background: '#fff',
            borderRadius: 8,
            boxShadow: '0 6px 20px rgba(0,0,0,0.15), 0 1px 4px rgba(0,0,0,0.1)',
            border: '1px solid #f0f0f0',
            padding: '4px 0',
            zIndex: 10000,
          }}
        >
          {items.map((item, idx) => (
            <React.Fragment key={item.key}>
              {item.danger && idx > 0 && (
                <div style={{ height: 1, background: '#f0f0f0', margin: '4px 0' }} />
              )}
              <div
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAction(item.key);
                }}
                style={{
                  padding: '7px 14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  color: item.danger ? '#ff4d4f' : '#262626',
                  transition: 'background 0.12s',
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = item.danger ? '#fff1f0' : '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                }}
              >
                <span style={{ width: 16, textAlign: 'center', fontSize: 13, opacity: 0.8 }}>
                  {item.icon}
                </span>
                <span>{item.label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
};

export default NodeContextMenu;
